import { Component, HostListener, inject, Input, OnInit } from '@angular/core'
import {
  AddAgendaNodeData,
  Agenda,
  AgendaNode,
  AgendaItemSubCategory,
  CallsService,
  MoveAgendaNodeData,
  RawAgendaItem,
  UpdateAgendaItemData,
  AgendaItemCategory,
} from '../../calls.service'
import { AgendaEditorItemComponent } from '../agenda-editor-item/agenda-editor-item.component'
import { MutationResult } from '@ngneat/query'
import { MatIcon } from '@angular/material/icon'
import { MatButton, MatIconButton } from '@angular/material/button'
import { TranslateModule, TranslateService } from '@ngx-translate/core'
import { MatTooltip } from '@angular/material/tooltip'
import { MatProgressSpinner } from '@angular/material/progress-spinner'
import { SpinnerComponent } from '../../spinner/spinner.component'
import { ConfirmDialogComponent } from '../../confirm-dialog/confirm-dialog.component'
import { MatDialog } from '@angular/material/dialog'
import { ActivatedRoute, Router } from '@angular/router'
import { AsyncPipe } from '@angular/common'
import { distinctUntilChanged, map, take } from 'rxjs'
import { moveItemInArray } from '@angular/cdk/drag-drop'
import { RemoveItemWithChildrenConfirmDialogComponent } from '../agenda-editor-item/remove-node-with-children-confirm-dialog/remove-item-with-children-confirm-dialog.component'

@Component({
  selector: 'app-agenda-editor',
  standalone: true,
  imports: [AgendaEditorItemComponent, MatIcon, MatIconButton, TranslateModule, MatTooltip, MatProgressSpinner, SpinnerComponent, AsyncPipe, MatButton],
  templateUrl: './agenda-editor.component.html',
  styleUrl: './agenda-editor.component.scss',
})
export class AgendaEditorComponent implements OnInit {
  @Input({ required: true })
  callId!: number

  @Input()
  isReadonly = false

  @Input({ required: true })
  set agenda(agenda: Agenda) {
    if (this._agenda !== null && !this.isReadonly) {
      return
    }
    this._agenda = this.getAgendaWithParents(agenda)
  }

  get agenda() {
    return this._agenda!!
  }

  private getAgendaWithParents(agenda: AgendaNode[]) {
    return JSON.parse(JSON.stringify(agenda))
  }

  _agenda: Agenda | null = null

  callsService = inject(CallsService)
  translateService = inject(TranslateService)
  dialog = inject(MatDialog)
  route = inject(ActivatedRoute)
  router = inject(Router)
  createAgendaItemMutation!: MutationResult<RawAgendaItem, Error, AddAgendaNodeData>
  updateAgendaItemMutation!: MutationResult<RawAgendaItem, Error, UpdateAgendaItemData>
  deleteAgendaItemMutation!: MutationResult<void, Error, number>
  deleteAgendaItemButKeepChildrenMutation!: MutationResult<void, Error, number>
  moveAgendaItemUpMutation!: MutationResult<void, Error, MoveAgendaNodeData>
  moveAgendaItemDownMutation!: MutationResult<void, Error, MoveAgendaNodeData>
  indentAgendaItemMutation!: MutationResult<void, Error, number>
  outdentAgendaItemMutation!: MutationResult<void, Error, number>
  resetAgendaItemsMutation!: MutationResult<AgendaNode[], Error, unknown>
  isSavingBlocking = false
  isSavingNonBlocking = false
  activeItemId$ = this.route.queryParamMap.pipe(
    map((paramMap) => {
      const value = paramMap.get('activeItemId')
      if (value === null) {
        return null
      }
      return parseInt(value)
    }),
    distinctUntilChanged()
  )

  ngOnInit() {
    this.createAgendaItemMutation = this.callsService.createAgendaItemMutation(this.callId)
    this.updateAgendaItemMutation = this.callsService.updateAgendaItemMutation(this.callId)
    this.deleteAgendaItemMutation = this.callsService.deleteAgendaItemMutation(this.callId)
    this.deleteAgendaItemButKeepChildrenMutation = this.callsService.deleteAgendaItemButKeepChildrenMutation(this.callId)
    this.moveAgendaItemUpMutation = this.callsService.moveAgendaItemUpMutation(this.callId)
    this.moveAgendaItemDownMutation = this.callsService.moveAgendaItemDownMutation(this.callId)
    this.indentAgendaItemMutation = this.callsService.indentAgendaItemMutation(this.callId)
    this.outdentAgendaItemMutation = this.callsService.outdentAgendaItemMutation(this.callId)
    this.resetAgendaItemsMutation = this.callsService.resetAgendaItemsMutation(this.callId)
  }

  setActiveItem(nodeId: number) {
    this.router.navigate([], { queryParams: { activeItemId: nodeId }, replaceUrl: true })
  }

  private findNodeById(id: number): AgendaNode {
    return this.findNodeRecursive(id, this.agenda)!
  }

  private findNodeRecursive(id: number, agenda: AgendaNode[]): AgendaNode | null {
    for (const item of agenda) {
      if (item.id === id) {
        return item
      }
      const found = this.findNodeRecursive(id, item.subitems)
      if (found !== null) {
        return found
      }
    }
    return null
  }

  async addNode(nodeId: number | null): Promise<void> {
    const node = nodeId !== null ? this.findNodeById(nodeId) : null
    const siblings = node !== null && node.parentId !== null ? this.findNodeById(node.parentId).subitems : this.agenda
    const currentNodeIndex = siblings.findIndex((child: AgendaNode) => child.id === nodeId)
    const newItemIndex = currentNodeIndex + 1
    this.isSavingNonBlocking = true
    const titleLabel = this.translateService.instant('CALLS.AGENDA_EDITOR.NEW_ITEM_TITLE')
    const parentId = node?.parentId ?? null
    const createdAgendaItem = await this.createAgendaItemMutation.mutateAsync({
      index: newItemIndex,
      title: titleLabel,
      parentId: parentId,
      category: AgendaItemCategory.SUBSTANTIVE,
      subCategories: [AgendaItemSubCategory.OTHER_SUBSTANTIVE],
    })
    siblings.splice(newItemIndex, 0, {
      id: createdAgendaItem.id,
      title: createdAgendaItem.title,
      subitems: [],
      parentId: parentId,
      itemNumber: createdAgendaItem.itemNumber,
      category: createdAgendaItem.category,
      subCategories: createdAgendaItem.subCategories,
      presenters: createdAgendaItem.presenters ?? '',
    })
    await this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {
        activeItemId: createdAgendaItem.id,
      },
      replaceUrl: true,
    })
    this.isSavingNonBlocking = false
  }

  async removeNode(nodeId: number): Promise<void> {
    const node = this.findNodeById(nodeId)
    const hasChildren = node.subitems.length > 0
    if (hasChildren) {
      const dialogRef = this.dialog.open(RemoveItemWithChildrenConfirmDialogComponent)

      dialogRef.afterClosed().subscribe(async (result) => {
        if (result === null) {
          return
        } else if (result === 'remove') {
          await this.actuallyRemoveNode(node)
        } else if (result === 'keep') {
          await this.removeNodeButKeepChildren(node)
        }
      })
      return
    } else {
      await this.actuallyRemoveNode(node)
    }
  }

  private async actuallyRemoveNode(node: AgendaNode) {
    this.isSavingNonBlocking = true
    await this.removeActiveItemId()
    const siblings = this.getSiblings(node)
    const index = siblings.findIndex((child: AgendaNode) => child.id === node.id)
    siblings.splice(index, 1)
    await this.deleteAgendaItemMutation.mutateAsync(node.id)
    this.isSavingNonBlocking = false
  }

  private async removeNodeButKeepChildren(node: AgendaNode) {
    this.isSavingNonBlocking = true
    await this.removeActiveItemId()
    const siblings = this.getSiblings(node)
    const index = siblings.findIndex((child: AgendaNode) => child.id === node.id)
    const parentId = node.parentId !== null ? this.findNodeById(node.parentId).parentId : null
    const children = node.subitems.map((child) => ({ ...child, parentId }))
    siblings.splice(index, 1, ...children)
    await this.deleteAgendaItemButKeepChildrenMutation.mutateAsync(node.id)
    this.isSavingNonBlocking = false
  }

  async moveNode(nodeId: number, direction: 'up' | 'down'): Promise<void> {
    const node = this.findNodeById(nodeId)
    const siblings = this.getSiblings(node)
    const oldIndex = siblings.findIndex((child: AgendaNode) => child.id === nodeId)
    const newIndex = direction === 'up' ? oldIndex - 1 : oldIndex + 1
    if (oldIndex === 0 && direction === 'up') {
      await this.outdent(nodeId)
      await this.moveUp(nodeId)
      return
    } else if (oldIndex === siblings.length - 1 && direction === 'down') {
      await this.outdent(nodeId)
      await this.moveDown(nodeId)
      return
    }
    moveItemInArray(siblings, oldIndex, newIndex)
    this.isSavingNonBlocking = true
    if (direction === 'up') {
      await this.moveAgendaItemUpMutation.mutateAsync({ nodeId: node.id, index: newIndex })
    } else {
      await this.moveAgendaItemDownMutation.mutateAsync({ nodeId: node.id, index: newIndex })
    }
    this.isSavingNonBlocking = false
  }

  async updateNode(node: AgendaNode) {
    this.isSavingNonBlocking = true
    await this.updateAgendaItemMutation.mutateAsync({
      id: node.id,
      title: node.title,
      itemNumber: node.itemNumber,
      category: node.category,
      subCategories: node.subCategories,
      presenters: node.presenters,
    })
    this.isSavingNonBlocking = false
  }

  reset() {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: this.translateService.instant('CALLS.AGENDA_EDITOR.RESET_DIALOG.TITLE'),
        description: this.translateService.instant('CALLS.AGENDA_EDITOR.RESET_DIALOG.DESCRIPTION'),
      },
    })

    dialogRef.afterClosed().subscribe(async (result) => {
      if (result) {
        await this.actuallyReset()
      }
    })
  }

  async actuallyReset() {
    this.isSavingBlocking = true
    await this.removeActiveItemId()
    const agenda = await this.resetAgendaItemsMutation.mutateAsync({})
    this._agenda = this.getAgendaWithParents(agenda)
    await this.removeActiveItemId()
    this.isSavingBlocking = false
  }

  private async removeActiveItemId() {
    await this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {
        activeItemId: null,
      },
      replaceUrl: true,
    })
  }

  async addSubitem(nodeId: number) {
    const node = this.findNodeById(nodeId)
    const newItemIndex = node.subitems.length
    this.isSavingNonBlocking = true
    const titleLabel = this.translateService.instant('CALLS.AGENDA_EDITOR.NEW_ITEM_TITLE')
    const createdAgendaItem = await this.createAgendaItemMutation.mutateAsync({
      index: newItemIndex,
      title: titleLabel,
      parentId: node.id,
      category: AgendaItemCategory.SUBSTANTIVE,
      subCategories: [AgendaItemSubCategory.OTHER_SUBSTANTIVE],
    })
    node.subitems.splice(newItemIndex, 0, {
      id: createdAgendaItem.id,
      title: createdAgendaItem.title,
      subitems: [],
      parentId: node.id,
      itemNumber: createdAgendaItem.itemNumber,
      category: createdAgendaItem.category,
      subCategories: createdAgendaItem.subCategories,
      presenters: createdAgendaItem.presenters ?? '',
    })
    this.isSavingNonBlocking = false
  }

  async moveUp(nodeId: number) {
    await this.moveNode(nodeId, 'up')
  }

  async moveDown(nodeId: number) {
    await this.moveNode(nodeId, 'down')
  }

  canMoveUp(nodeId: number) {
    const node = this.findNodeById(nodeId)
    const siblings = this.getSiblings(node)
    const index = siblings.findIndex((child: AgendaNode) => child.id === nodeId)
    return (index > 0 && node.parentId === null) || node.parentId !== null
  }

  canMoveDown(nodeId: number) {
    const node = this.findNodeById(nodeId)
    const siblings = this.getSiblings(node)
    const index = siblings.findIndex((child: AgendaNode) => child.id === nodeId)
    return (index < siblings.length - 1 && node.parentId === null) || node.parentId !== null
  }

  private getSiblings(node: AgendaNode): Agenda {
    return node.parentId !== null ? this.findNodeById(node.parentId).subitems : this.agenda
  }

  canIndent(nodeId: number) {
    const node = this.findNodeById(nodeId)
    const siblings = this.getSiblings(node)
    const index = siblings.findIndex((child: AgendaNode) => child.id === nodeId)
    return index > 0
  }

  canOutdent(nodeId: number) {
    const node = this.findNodeById(nodeId)
    return node.parentId !== null
  }

  async indent(nodeId: number) {
    const node = this.findNodeById(nodeId)
    const siblings = this.getSiblings(node)
    const oldIndex = siblings.findIndex((child: AgendaNode) => child.id === nodeId)
    if (oldIndex === 0) {
      return
    }
    const newParent = siblings[oldIndex - 1]
    siblings.splice(oldIndex, 1)
    newParent.subitems.push(node)
    node.parentId = newParent.id
    this.isSavingNonBlocking = true
    await this.indentAgendaItemMutation.mutateAsync(node.id)
    this.isSavingNonBlocking = false
  }

  async outdent(nodeId: number) {
    const node = this.findNodeById(nodeId)
    const parent = this.findNodeById(node.parentId!)
    const parentSiblings = this.getSiblings(parent)
    const oldIndex = parent.subitems.findIndex((child: AgendaNode) => child.id === node.id)
    parent.subitems.splice(oldIndex, 1)
    const newIndex = parentSiblings.findIndex((child: AgendaNode) => child.id === node.parentId)
    parentSiblings.splice(newIndex + 1, 0, node)
    node.parentId = parent.parentId
    this.isSavingNonBlocking = true
    await this.outdentAgendaItemMutation.mutateAsync(node.id)
    this.isSavingNonBlocking = false
  }

  @HostListener('document:keydown', ['$event'])
  handleKeydown(event: KeyboardEvent) {
    if (event.key === 'Delete') {
      event.preventDefault()
      this.activeItemId$.pipe(take(1)).subscribe(async (activeItemId) => {
        if (activeItemId !== null) {
          await this.removeNode(activeItemId)
        }
      })
    }
  }
}
